import { Component, inject, Signal, Renderer2 } from '@angular/core';
import { RouterModule } from '@angular/router';
import { PopoverShownEvent } from '@progress/kendo-angular-tooltip';

import { StandaloneDefaultImports } from 'src/app/shared/ui/standalone-default-imports';
import { kendoTooltipsDefaultImports } from 'src/app/shared/ui/kendo-default-imports';
import { GlobalStateService } from 'src/app/core/state/global-state.service';
import { AuthUserStateService } from 'src/app/core/state/auth-user-state.service';
import { AuthService } from 'src/app/core/services/authentication.service';
import { NavigationService } from 'src/app/core/services/navigation.service';
import { UserAvatarComponent } from 'src/app/shared/components/user-avatar/user-avatar.component';
import { buildLoginRedirectUrl } from 'src/app/core/utils/url-utils';
import { LanguageService } from 'src/app/core/services/language.service';
import { NewFeaturesService } from 'src/app/core/services/new-features.service';

@Component({
    selector: 'header-top-menu',
    imports: [...StandaloneDefaultImports, ...kendoTooltipsDefaultImports, UserAvatarComponent, RouterModule],
    templateUrl: './header-top-menu.html',
    styleUrls: ['./_header-top-menu.scss']
})
export class HeaderTopMenuComponent {
    // Section: SERVICES
    //----------------------------------------------------------------------
    private globalState = inject(GlobalStateService);
    private authUserState = inject(AuthUserStateService);
    private authService = inject(AuthService);
    private navigationService = inject(NavigationService);
    private languageService = inject(LanguageService);
    private renderer = inject(Renderer2);
    private newFeaturesService = inject(NewFeaturesService);

    // Section: SIGNALS, OBSERVABLES AND STATE REACTIVE
    //----------------------------------------------------------------------
    isHeaderHidden: Signal<boolean> = this.globalState.isHeaderHidden;
    authenticated: Signal<boolean> = this.authUserState.isAuthenticated;

    // Section: PUBLIC PROPERTIES
    //----------------------------------------------------------------------
    loggedUser = this.authUserState.user;
    readonly userLoggedHasAdminRole = this.authUserState.isAdmin;

    get privacyPolicyUrlBasedOnLang(): string {
        return this.getLegalDocumentUrl('/privacy-policy');
    }

    get termsAndCondsUrlBasedOnLang(): string {
        return this.getLegalDocumentUrl('/terms-and-conditions');
    }

    get dataTreatmentUrlBasedOnLang(): string {
        return this.getLegalDocumentUrl('/personal-data-treatment');
    }

    // Section: PUBLIC METHODS
    //----------------------------------------------------------------------
    openNewFeaturesAdvice(): void {
        this.newFeaturesService.checkForNewFeaturesAdvice('popover');
    }

    openInNewTab(url: string): void {
        this.navigationService.openExternalUrl(url);
    }

    goToDialengaFeedbackForm(): void {
        const userId = this.loggedUser()?.id;
        const feedbackUrl = `https://dialenga.typeform.com/to/DqN4uWgY#id_usuario=${userId}`;
        this.openInNewTab(feedbackUrl);
    }

    /**
     * Handles the event triggered when a popover is shown.
     * Adds a specified CSS class to the popover's popup element.
     *
     * @param event - The event object containing details about the popover being shown.
     * @param className - The CSS class name to be added to the popover's popup element.
     */
    onPopoverShown(event: PopoverShownEvent, className: string): void {
        const popupElement = event.popover?.popupElement;
        if (popupElement) {
            this.renderer.addClass(popupElement, className);
        }
    }

    redirectTo(url: string) {
        this.navigationService.navigateToUrl(url);
    }

    userLogout(): void {
        this.authService.logout().subscribe({
            next: () => {
                const companyCodeFromStorage = localStorage.getItem('lastUserLoginBrand');
                const loginUrl = buildLoginRedirectUrl('/login', companyCodeFromStorage);
                this.redirectTo(loginUrl);
            },
            error: (err) => console.error('Logout failed', err)
        });
    }

    /**
     * Skips to the main content of the page by setting focus on the element with the ID 'main-content'.
     * This method is typically used to improve accessibility by allowing users to bypass repetitive
     * navigation links and directly access the main content.
     *
     * @param event - The event object associated with the skip action, typically a click or keypress event.
     */
    skipToContent(event: Event) {
        event.preventDefault();

        const main = document.getElementById('main-content');
        if (!main) return;

        this.renderer.setAttribute(main, 'tabindex', '-1');
        main.focus();
    }

    // Section: PRIVATE METHODS
    //----------------------------------------------------------------------
    private getLegalDocumentUrl(legalDoc: string): string {
        switch (this.languageService.getCurrentLanguage()) {
            case 'es':
            case 'ca_ES':
            case 'eu_ES':
            case 'es_MX':
                return legalDoc;
            case 'fr':
                return `${legalDoc}-fr`;
            default:
                return `${legalDoc}-en`;
        }
    }
}
